/**
 * Topic Manager (frontend)
 * Provides access to topic catalog operations via the DailyReportAPI /topics endpoint
 */
import { getCurrentUser } from '@aws-amplify/auth';
import { apiClient } from './apiClient';

class TopicManager {
  constructor() {
    // No configuration needed - apiClient handles everything
  }

  /**
   * List all topics
   * @param {number} limit - Maximum number of topics to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Object[]>} Array of topics
   */
  async listTopics(limit = 100, offset = 0) {
    const data = await apiClient.topics({ operation: 'listTopics', limit, offset });
    return data.topics || [];
  }

  /**
   * Search topics by prefix
   * @param {string} prefix - Search prefix (empty string lists all)
   * @param {number} limit - Maximum number of topics to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Object[]>} Array of matching topics
   */
  async searchTopics(prefix = '', limit = 25, offset = 0) {
    // If prefix is empty, use listTopics instead of searchTopics
    if (!prefix || prefix.trim() === '') {
      return await this.listTopics(limit, offset);
    }
    
    const data = await apiClient.topics({ operation: 'searchTopics', prefix, limit, offset });
    return data.topics || [];
  }

  async createTopic(name, fileReference) {
    if (!fileReference) {
      throw new Error('File reference is required for topics');
    }
    const user = await getCurrentUser();
    const data = await apiClient.topics({ 
      operation: 'createTopic', 
      name, 
      fileReference, 
      cognitoSub: user.userId 
    });
    return data.topic;
  }

  async updateTopic(id, name, fileReference) {
    const user = await getCurrentUser();
    const data = await apiClient.topics({ 
      operation: 'updateTopic', 
      id, 
      name, 
      fileReference, 
      cognitoSub: user.userId 
    });
    return data;
  }

  async setTopicStatus(id, status) {
    const user = await getCurrentUser();
    const data = await apiClient.topics({ operation: 'setTopicStatus', id, status, cognitoSub: user.userId });
    return data;
  }

  async deleteTopic(id) {
    const user = await getCurrentUser();
    const data = await apiClient.topics({ operation: 'deleteTopic', id, cognitoSub: user.userId });
    return data;
  }

  async getTopicFileContent(topicId) {
    const user = await getCurrentUser();
    const data = await apiClient.topics({ 
      operation: 'getTopicFileContent', 
      topicId, 
      cognitoSub: user.userId 
    });
    return data;
  }
}

export const topicManager = new TopicManager();
export default topicManager;
